import { NewListingBadge } from "@/components/property/new-listing-badge";
import { useEffect, useMemo, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useLocation } from "wouter";
import { Search, ArrowRight } from "lucide-react";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import type { BlogPostWithCategory, PublicProperty } from "@shared/schema";

/**
 * One search box for the whole site: the 116 travel guide articles, both
 * homes, and the pages people ask for by name. Client side, so results
 * appear as you type; Enter opens the first one.
 */

type Hit = { title: string; detail: string; href: string; kind: "guide" | "home" | "page"; slug?: string };

const PAGES: Hit[] = [
  { title: "Our Homes", detail: "Compare both homes and check dates", href: "/properties", kind: "page" },
  { title: "Experiences", detail: "Tours and activities near the homes", href: "/experiences", kind: "page" },
  { title: "Travel Guide", detail: "Guides to beaches, hikes, food and planning", href: "/blog", kind: "page" },
  { title: "Ka'a'awa area guide", detail: "The windward coast around the homes", href: "/kaaawa", kind: "page" },
  { title: "FAQ", detail: "Common questions about booking and the stay", href: "/faq", kind: "page" },
  { title: "House rules", detail: "Quiet hours, guests, water equipment", href: "/house-rules", kind: "page" },
  { title: "Cancellation policy and terms", detail: "Refunds, deposits and terms", href: "/policies", kind: "page" },
  { title: "Contact", detail: "Message the hosts", href: "/contact", kind: "page" },
  { title: "About the hosts", detail: "Who we are", href: "/about", kind: "page" },
];

function score(hit: Hit, q: string): number {
  const t = hit.title.toLowerCase();
  const d = hit.detail.toLowerCase();
  if (t === q) return 100;
  if (t.startsWith(q)) return 80;
  if (t.includes(q)) return 60;
  const words = q.split(/\s+/).filter(Boolean);
  const inTitle = words.filter((w) => t.includes(w)).length;
  const inDetail = words.filter((w) => d.includes(w)).length;
  if (inTitle === words.length) return 50;
  if (inTitle + inDetail >= words.length) return 30;
  if (inTitle > 0) return 20;
  if (inDetail > 0) return 10;
  return 0;
}

export function SiteSearch({ open, onOpenChange }: { open: boolean; onOpenChange: (open: boolean) => void }) {
  const [, setLocation] = useLocation();
  const [q, setQ] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);

  const { data: posts = [] } = useQuery<BlogPostWithCategory[]>({ queryKey: ["/api/blog/posts"], enabled: open, staleTime: 30 * 60 * 1000 });
  const { data: homes = [] } = useQuery<PublicProperty[]>({ queryKey: ["/api/properties"], enabled: open, staleTime: 30 * 60 * 1000 });

  useEffect(() => {
    if (open) {
      setQ("");
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [open]);

  const hits = useMemo(() => {
    const query = q.trim().toLowerCase();
    if (!query) return [];
    const all: Hit[] = [
      ...homes.filter((h) => h.slug).map((h) => ({
        title: h.title,
        detail: `${h.bedrooms ?? ""} bedrooms · up to ${h.max_guests ?? ""} guests · ${h.public_location_label || h.general_location || "Ka'a'awa"}`,
        href: `/properties/${h.slug}`,
        kind: "home" as const,
        slug: h.slug ?? undefined,
      })),
      ...PAGES,
      ...posts.map((p) => ({
        title: p.title,
        detail: (p.excerpt || p.category?.name || "").replace(/\s+/g, " ").slice(0, 120),
        href: `/blog/${p.slug}`,
        kind: "guide" as const,
      })),
    ];
    return all
      .map((h) => ({ h, s: score(h, query) }))
      .filter((x) => x.s > 0)
      .sort((a, b) => b.s - a.s || (a.h.kind === "guide" ? 1 : -1))
      .slice(0, 12)
      .map((x) => x.h);
  }, [q, posts, homes]);

  const go = (href: string) => {
    onOpenChange(false);
    setLocation(href);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="top-[12%] translate-y-0 max-w-xl gap-0 border-sand-deep bg-shell p-0 overflow-hidden">
        <DialogTitle className="sr-only">Search the site</DialogTitle>
        <form
          className="flex items-center gap-3 border-b border-sand-deep px-4"
          onSubmit={(e) => {
            e.preventDefault();
            if (hits[0]) go(hits[0].href);
          }}
        >
          <Search className="h-4 w-4 shrink-0 text-driftwood" aria-hidden="true" />
          <input
            ref={inputRef}
            type="search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search beaches, hikes, the homes, house rules…"
            aria-label="Search the site"
            className="h-14 w-full bg-transparent font-sans text-[16px] text-ink placeholder:text-ink/40 focus:outline-none"
            data-testid="site-search-input"
          />
        </form>
        <div className="max-h-[60vh] overflow-y-auto" data-testid="site-search-results">
          {q.trim() && hits.length === 0 && (
            <p className="px-5 py-6 font-sans text-[14px] text-ink-soft">Nothing matches that. Try a beach, a trail, or a question about the stay.</p>
          )}
          {!q.trim() && (
            <div className="px-5 py-4">
              <p className="font-sans text-[10px] uppercase tracking-[0.28em] text-driftwood">Popular</p>
              <ul className="mt-2 divide-y divide-sand-deep/60">
                {PAGES.slice(0, 5).map((p) => (
                  <li key={p.href}>
                    <button type="button" onClick={() => go(p.href)} className="flex w-full items-center justify-between py-2.5 text-left font-sans text-[15px] text-ink hover:text-forest">
                      {p.title} <ArrowRight className="h-3.5 w-3.5 text-driftwood" />
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          )}
          {hits.length > 0 && (
            <ul className="divide-y divide-sand-deep/60">
              {hits.map((h) => (
                <li key={h.href}>
                  <button
                    type="button"
                    onClick={() => go(h.href)}
                    className="flex w-full items-start gap-3 px-5 py-3 text-left hover:bg-sand/60"
                    data-testid={`site-search-hit-${h.kind}`}
                  >
                    <span className="mt-1 shrink-0 rounded-sm border border-sand-deep px-1.5 py-0.5 font-sans text-[9px] uppercase tracking-[0.18em] text-driftwood">
                      {h.kind === "guide" ? "Guide" : h.kind === "home" ? "Home" : "Page"}
                    </span>
                    <span className="min-w-0">
                      <span className="block truncate font-serif text-[17px] text-ink">{h.title} <NewListingBadge slug={h.slug} /></span>
                      {h.detail && <span className="block truncate font-sans text-[12.5px] text-ink-soft">{h.detail}</span>}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
